import { Box, FormControl, Typography } from '@mui/material'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import DateTimeInput from './DateTimeInput'
import SearchAndSelectInput from './SearchAndSelectInput'
import { useAppSelector } from '../../redux/hook'
import dayjs from 'dayjs'
import { getAllTimezoneOptions } from '../../utils/TimezoneOptions'

interface TimeAndZoneSelectProps {
    id: string
    label: string
    value: any
    onChange: any
    required?: boolean
    clearable?: boolean
    disabled?: boolean
    errorMessage?: string
}

type DateTimeValueType = {
    datetime: string | null
    timezone: string | null
}

const TimeAndZoneSelect = (props: TimeAndZoneSelectProps): ReactElement => {
    const {
        id,
        label,
        value,
        onChange,
        required,
        clearable = false,
        disabled,
        errorMessage = '',
    } = props

    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )
    const displayAM_PM = userLocalization?.timeFormat.startsWith('hh:mm')
    let userPreferredDateFormat = 'MM/DD/YYYY HH:mm'
    if (userLocalization?.dateFormat && userLocalization?.timeFormat) {
        const timeFormat = userLocalization?.timeFormat
            ?.split(':')
            .slice(0, 2)
            .join(':')
        const A = userLocalization?.timeFormat?.includes('A') ? 'A' : ''
        userPreferredDateFormat = `${userLocalization.dateFormat} ${timeFormat} ${A}`
    }
    const userPreferredTimeZone =
        userLocalization?.timeZone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone

    const [dateTimeValue, setDateTimeValue] =
        useState<DateTimeValueType | null>(null)

    const [timeZoneList, setTimeZoneList] = useState<any[]>([])

    // get the options for timezones
    const initialTimeZoneList = useMemo(() => {
        return getAllTimezoneOptions().map((opt) => ({
            id: opt.value,
            name: opt.label,
        }))
    }, [])

    useEffect(() => {
        setTimeZoneList(initialTimeZoneList)
    }, [initialTimeZoneList])

    useEffect(() => {
        if (value) {
            setDateTimeValue(value)
        } else {
            setDateTimeValue(null)
        }
    }, [value])

    const onChangeTime = (id: string, val: string) => {
        const updatedVal = {
            datetime: val,
            timezone:
                dateTimeValue?.timezone ||
                userPreferredTimeZone ||
                dayjs.tz.guess(),
        }
        setDateTimeValue(updatedVal)
        onChange(id, updatedVal)
    }
    const onSelectTimeZone = (id: string, value: string) => {
        const updatedVal = {
            datetime: dateTimeValue?.datetime || '',
            timezone: value,
        }
        setDateTimeValue(updatedVal)
        onChange(id, updatedVal)
    }
    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth>
                <Typography sx={{ opacity: '0.7', fontSize: '14px' }}>
                    {label}
                    {required ? '*' : ''}
                </Typography>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ width: 'calc(55%)' }}>
                        <DateTimeInput
                            label={''}
                            id={id}
                            value={dateTimeValue?.datetime || ''}
                            onChange={onChangeTime}
                            errorMessage={
                                !dateTimeValue?.datetime && errorMessage
                                    ? errorMessage
                                    : ''
                            }
                            disabled={disabled}
                            clearable={clearable}
                            dateFormat={userPreferredDateFormat}
                            displayAM_PM={displayAM_PM}
                        />
                    </Box>
                    <Box sx={{ width: 'calc(45%)' }}>
                        <SearchAndSelectInput
                            label=""
                            id={id}
                            value={dateTimeValue?.timezone || ''}
                            list={timeZoneList}
                            list_Height="200px"
                            disabled={disabled}
                            onSelect={onSelectTimeZone}
                            hasEmpty={false}
                            errorMessage={
                                !dateTimeValue?.timezone && errorMessage
                                    ? errorMessage
                                    : ''
                            }
                        />
                    </Box>
                </Box>
            </FormControl>
        </Box>
    )
}

export default TimeAndZoneSelect
